/*
 * The Belgian Brewer: Private hire (sections/tbb-hire.liquid).
 * - Accordion: at 1100px and wider, hover, keyboard focus or a click opens a panel (the others narrow).
 *   Below 1100px every panel is open and the buttons are just headings.
 * - "Enquire" links preselect their space in the form.
 * - Spaces at the venue picked in "Venue with its own occasion choices" swap in that venue's
 *   occasion list (e.g. "Private tasting" at Epping instead of the brewery tour).
 * - Venue (required): picking a venue narrows the space list to that venue (its only space is
 *   picked for you); picking a space, or clicking its "Enquire" link, fills in its venue.
 *   Choosing that venue alone also swaps in its occasion list.
 * - Inline validation for venue, name and email before the form posts; after a successful post the
 *   thank-you message is scrolled into view and focused.
 */
function setUpAccordion(root) {
  const accordion = root.querySelector('[data-tbb-accordion]');
  if (!accordion) return;
  const slices = [...accordion.querySelectorAll('[data-tbb-slice]')];
  if (!slices.length) return;
  const wide = window.matchMedia('(min-width: 1100px)');
  let current = accordion.querySelector('.is-open') || slices[0];

  const paint = () =>
    slices.forEach((slice) => {
      slice.classList.toggle('is-open', slice === current);
      slice.querySelector('.tbb-hire__btn')?.setAttribute('aria-expanded', String(!wide.matches || slice === current));
    });
  const open = (slice) => {
    if (slice !== current) {
      current = slice;
      paint();
    }
  };

  slices.forEach((slice) => {
    slice.addEventListener('mouseenter', () => {
      if (wide.matches) open(slice);
    });
    slice.addEventListener('focusin', () => open(slice));
    slice.addEventListener('click', () => open(slice));
  });
  wide.addEventListener('change', paint);
  paint();
}

function setUpForm(root) {
  const form = root.querySelector('form#enquire');
  const select = root.querySelector('[data-tbb-space-select]');

  root.querySelectorAll('[data-tbb-space]').forEach((link) =>
    link.addEventListener('click', () => {
      if (select && [...select.options].some((o) => o.value === link.dataset.tbbSpace)) {
        select.value = link.dataset.tbbSpace;
        select.dispatchEvent(new Event('change'));
      }
    })
  );

  const done = root.querySelector('[data-tbb-enquiry-done]');
  if (done) {
    done.scrollIntoView({ block: 'center' });
    done.focus({ preventScroll: true });
  }

  if (!form) return;
  const emailPattern = /^\S+@\S+\.\S+$/;

  form.addEventListener('submit', (event) => {
    let firstInvalid = null;
    form.querySelectorAll('[data-tbb-required]').forEach((field) => {
      const value = field.value.trim();
      const ok = field.type === 'email' ? emailPattern.test(value) : value !== '';
      const message = document.getElementById(field.getAttribute('aria-describedby'));
      field.setAttribute('aria-invalid', String(!ok));
      if (message) message.textContent = ok ? '' : field.dataset.tbbRequired;
      if (!ok && !firstInvalid) firstInvalid = field;
    });
    if (firstInvalid) {
      event.preventDefault();
      firstInvalid.focus();
    }
  });
}

function setUpVenues(root) {
  const venueSelect = root.querySelector('[data-tbb-venue-select]');
  const spaceSelect = root.querySelector('[data-tbb-space-select]');
  if (!venueSelect || !spaceSelect) return;
  const spaces = [...spaceSelect.options].filter((option) => option.dataset.tbbVenue);

  const clearError = () => {
    venueSelect.setAttribute('aria-invalid', 'false');
    const message = document.getElementById(venueSelect.getAttribute('aria-describedby'));
    if (message) message.textContent = '';
  };

  // Hide (and disable, for browsers that ignore hidden options) spaces at the other venue
  const narrow = (venue, pickOnly) => {
    spaces.forEach((option) => {
      const away = venue !== '' && option.dataset.tbbVenue !== venue;
      option.hidden = away;
      option.disabled = away;
    });
    const current = spaceSelect.selectedOptions[0];
    if (current?.disabled) spaceSelect.value = 'unsure';
    const here = spaces.filter((option) => !option.disabled);
    if (pickOnly && venue !== '' && here.length === 1 && spaceSelect.value === 'unsure') {
      spaceSelect.value = here[0].value;
    }
  };

  venueSelect.addEventListener('change', () => {
    if (venueSelect.value) clearError();
    narrow(venueSelect.value, true);
    spaceSelect.dispatchEvent(new Event('change'));
  });

  spaceSelect.addEventListener('change', () => {
    const venue = spaceSelect.selectedOptions[0]?.dataset.tbbVenue;
    if (venue && venueSelect.value !== venue) {
      venueSelect.value = venue;
      clearError();
      narrow(venue, false);
    }
  });

  // A space restored by the browser (back button) brings its venue with it
  const restored = spaceSelect.selectedOptions[0]?.dataset.tbbVenue;
  if (restored) venueSelect.value = restored;
  narrow(venueSelect.value, false);
}

function setUpOccasions(root) {
  const spaceSelect = root.querySelector('[data-tbb-space-select]');
  const venueSelect = root.querySelector('[data-tbb-venue-select]');
  const occasionSelect = root.querySelector('[data-tbb-occasion-select]');
  const venueList = root.querySelector('template[data-tbb-venue-occasions]');
  if (!spaceSelect || !occasionSelect || !venueList) return;
  const lists = {
    main: [...occasionSelect.options].map((option) => option.cloneNode(true)),
    venue: [...venueList.content.querySelectorAll('option')],
  };
  let shown = 'main';

  const sync = () => {
    const atVenue = (select) => select?.selectedOptions[0]?.hasAttribute('data-tbb-venue-occasions');
    const wanted = atVenue(spaceSelect) || atVenue(venueSelect) ? 'venue' : 'main';
    if (wanted === shown) return;
    const picked = occasionSelect.value;
    const index = occasionSelect.selectedIndex;
    const options = lists[wanted].map((option) => option.cloneNode(true));
    occasionSelect.replaceChildren(...options);
    // Keep the same choice if both lists have it, otherwise the one in the same position
    const same = options.findIndex((option) => option.value === picked);
    occasionSelect.selectedIndex = same >= 0 ? same : Math.min(index, options.length - 1);
    shown = wanted;
  };

  spaceSelect.addEventListener('change', sync);
  venueSelect?.addEventListener('change', sync);
  sync();
}

function setUp(root) {
  if (root.dataset.tbbReady) return;
  root.dataset.tbbReady = 'true';
  setUpAccordion(root);
  setUpVenues(root);
  setUpForm(root);
  setUpOccasions(root);
}

const init = (root = document) => root.querySelectorAll('[data-tbb-hire]').forEach(setUp);
init();
document.addEventListener('shopify:section:load', (event) => init(event.target));
